import { useTranslation } from "react-i18next"

export interface OnboardingCharterBodyProps {
  /**
   * **Sanitized HTML — pre-stripped server-side at write time.**
   *
   * Threat model:
   *   - The charter is authored by Brand Admin via OKS-37's editor.
   *   - OKS-37 applies Rails' `Rails::Html::SafeListSanitizer` with a
   *     strict allow-list (`p`, `br`, `strong`, `em`, `ul`, `ol`, `li`,
   *     `h2`, `h3`, `a` — `a[href]` only, no `script`, no `on*`, no
   *     `style`) BEFORE persisting to the database.
   *   - OKS-22's `Onboarding::Signup` controller reads the already-safe
   *     `brand.crew_charter` field and ships it as `charter.rich_text_html`.
   *
   * Belt-and-braces FE re-sanitisation (DOMPurify) was considered and
   * rejected during TRD review:
   *   - +~20kB gzip on a public landing page where every byte counts on 4G.
   *   - Zero net safety win when BE enforces the same allow-list at the
   *     write boundary.
   *   - Adds a runtime dependency on a single brand-authored doc that
   *     never changes per render.
   *
   * If a future code-review reviewer flags this, the response is in BE
   * TRD §Approach + this comment block. Adding DOMPurify requires a
   * follow-up ticket (and CTPO call on the trade-off).
   */
  html: string
  className?: string
}

/**
 * OnboardingCharterBody — scrollable rich-text region for the brand's
 * crew charter (Step 2).
 *
 * Per UX Spec v3 + Pencil prototype `cTDGw`:
 *   - Card visual: `rounded-xl`, padding 20, `border-subtle` 1px stroke.
 *   - Scrollable region capped at 60vh (50vh on mobile per UX Spec §6).
 *   - `role="region"` + `tabIndex={0}` so keyboard users can scroll the
 *     content area without a mouse (UX Spec §5).
 *
 * Typography: Tailwind `prose` for sane defaults across the
 * `SafeListSanitizer` allow-list (`h2`, `h3`, `p`, `ul`, `ol`, `strong`,
 * `em`, `a`).
 */
export function OnboardingCharterBody({
  html,
  className = "",
}: OnboardingCharterBodyProps) {
  const { t } = useTranslation()
  return (
    <div
      // role + label + tabIndex per UX Spec §5 — keyboard-scrollable, SR
      // announces "Crew charter content" landmark.
      role="region"
      aria-label={t("signup.charter.heading", { brand: "" }).trim()}
      tabIndex={0}
      data-testid="onboarding-charter-body"
      className={[
        // Card visual per Pencil prototype `cTDGw`.
        "rounded-xl border p-5",
        "border-border/60",
        // Scrollable region — capped per UX Spec §6 responsive rules.
        "max-h-[50vh] overflow-y-auto sm:max-h-[60vh]",
        // Typography defaults via Tailwind Typography plugin (already in
        // Crew's stack); falls back to readable defaults if not.
        "prose prose-sm dark:prose-invert max-w-none",
        // Focus ring on the keyboard-focusable region.
        "focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
        className,
      ].join(" ")}
      // Sanitisation happens server-side at write time per BE TRD §Approach;
      // see prop docstring above for the full threat model + mitigation.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

export default OnboardingCharterBody
